/**
 * Buffered analytics (packages/schemas/src/analytics.ts): events are queued in memory,
 * persisted when the app hides, and flushed in batches of up to 200 when reachable.
 */
import type { AnalyticsEvent } from '@mp/schemas';
import type { ANALYTICS_EVENTS } from '@mp/schemas';
import { shortId } from './ids.js';
import type { KV } from './storage.js';

export type EventName = (typeof ANALYTICS_EVENTS)[number];
export type Props = Record<string, string | number | boolean | null>;

const KEY = 'analytics';
const MAX_BUFFER = 1000;
const BATCH = 200;

export interface AnalyticsDeps {
  kv: KV;
  send: ((events: AnalyticsEvent[]) => Promise<unknown>) | null;
  now: () => number;
  flushEvery?: number;
  /** SY-02: nothing leaves the device before the age check / consent allows it */
  allowed?: () => boolean;
}

export class Analytics {
  readonly sessionId = shortId(16);
  private buffer: AnalyticsEvent[];
  private flushing: Promise<void> | null = null;
  /** test/debug tap: every tracked event */
  readonly log: AnalyticsEvent[] = [];

  constructor(private d: AnalyticsDeps) {
    const saved = d.kv.get(KEY);
    this.buffer = saved ? (JSON.parse(saved) as AnalyticsEvent[]) : [];
    d.kv.remove(KEY);
  }

  get pending(): number {
    return this.buffer.length;
  }

  track(name: EventName, props: Props = {}): void {
    const clean: Props = {};
    for (const [k, v] of Object.entries(props)) clean[k] = typeof v === 'string' ? v.slice(0, 200) : v;
    const ev: AnalyticsEvent = { name, ts: this.d.now(), sessionId: this.sessionId, props: clean };
    this.buffer.push(ev);
    this.log.push(ev);
    if (this.log.length > 500) this.log.shift();
    if (this.buffer.length > MAX_BUFFER) this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    if (this.buffer.length >= (this.d.flushEvery ?? 25)) void this.flush();
  }

  flush(): Promise<void> {
    if (!this.d.send || !this.buffer.length || this.d.allowed?.() === false) return Promise.resolve();
    this.flushing ??= this.run().finally(() => (this.flushing = null));
    return this.flushing;
  }

  private async run(): Promise<void> {
    const send = this.d.send!;
    while (this.buffer.length) {
      const batch = this.buffer.slice(0, BATCH);
      const ok = await send(batch).then(
        () => true,
        () => false, // offline or API down: keep the events for the next flush
      );
      if (!ok) return;
      this.buffer.splice(0, batch.length);
    }
    this.d.kv.remove(KEY);
  }

  /** Consent withdrawn (SY-02.2): drop what has not been sent. */
  discard(): void {
    this.buffer = [];
    this.d.kv.remove(KEY);
  }

  /** Called when the page hides: keep unsent events across reloads. */
  persist(): void {
    if (this.buffer.length) this.d.kv.set(KEY, JSON.stringify(this.buffer));
    else this.d.kv.remove(KEY);
  }
}
